import { useEffect } from 'react';
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import {
    AudiotrackOutlined,
    Close,
    FolderOpenOutlined,
    GroupsOutlined,
    MusicNote,
    PersonOutlined,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store';
import { getUserProjects } from '../../features/sessions/projectsManagement/sessionManageProjects';
import { getLocalStorageAuthHeaders } from '../../features/sessions/sessionSlice';
import * as R from '../../api/requestRequirements';
import jinnerLogo from '../../assets/logo-white-name-white-without-background-portrait.png';

const BG = '#1c1c24';
const BG_HOVER = 'rgba(255,255,255,0.06)';
const TEXT = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.6)';
const BORDER = 'rgba(255,255,255,0.08)';
const BRAND_RED = '#c0392b';
const DRAWER_WIDTH = 260;

function SideBar({ close }: { close: () => void }) {
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const allProjects = useSelector((state: RootState) => state.sessionManageProjects.allProjects);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser?.nickname) return;
        dispatch(getUserProjects({
            authHeaders: getLocalStorageAuthHeaders(),
            urlParams: { userNickname: currentUser.nickname, page: 1 },
        }));
    }, [currentUser?.nickname]);

    const routes = currentUser
        ? R.handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser.nickname } })
        : null;

    function nav(path?: string) {
        if (!path) return;
        navigate(path);
        close();
    }

    const navItems = [
        { label: 'Profile',      icon: <PersonOutlined />,   path: routes?.USER },
        { label: 'My Contents',  icon: <AudiotrackOutlined />, path: routes?.HOME },
        { label: 'My Projects',  icon: <FolderOpenOutlined />, path: routes?.USER_PROJECTS },
        { label: 'Groups',       icon: <GroupsOutlined />,   path: routes?.MEMBER_GROUPS },
    ];

    const sortedProjects = [...allProjects].sort((a, b) =>
        new Date(b.project?.updatedAt || 0).getTime() -
        new Date(a.project?.updatedAt || 0).getTime()
    );

    return (
        <Drawer
            open
            onClose={close}
            variant="temporary"
            anchor="left"
            sx={{
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    bgcolor: BG,
                    border: 'none',
                    borderRight: `1px solid ${BORDER}`,
                    display: 'flex',
                    flexDirection: 'column',
                },
                '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.4)' },
            }}
        >
            {/* Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box component="img" src={jinnerLogo} alt="Jinner" sx={{ height: 36 }} />
                <IconButton onClick={close} size="small" sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT } }}>
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            <Divider sx={{ borderColor: BORDER, mb: 1 }} />

            {/* Navigation */}
            <List dense disablePadding sx={{ px: 1 }}>
                {navItems.map((item) => (
                    <ListItemButton
                        key={item.label}
                        onClick={() => nav(item.path)}
                        sx={{ borderRadius: 1.5, mb: 0.5, '&:hover': { bgcolor: BG_HOVER } }}
                    >
                        <ListItemIcon sx={{ color: TEXT_SECONDARY, minWidth: 36 }}>
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: 14, color: TEXT, fontWeight: 500 }}
                        />
                    </ListItemButton>
                ))}
            </List>

            <Divider sx={{ borderColor: BORDER, my: 1 }} />

            {/* Projects section */}
            <Box sx={{ px: 2, mb: 0.5 }}>
                <Typography variant="caption" sx={{
                    color: TEXT_SECONDARY,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontSize: 11,
                }}>
                    Projects
                </Typography>
            </Box>

            <List dense disablePadding sx={{ px: 1, flex: 1, overflowY: 'auto' }}>
                {sortedProjects.map((p) => {
                    const ownerNickname = p.project.user?.nickname || currentUser?.nickname;
                    const projectRoute = ownerNickname
                        ? R.handlePrivateRoutes({
                            ROUTE_PARAMS: { userNickname: ownerNickname, projectId: p.project.id },
                          })?.PROJECT_CONTENTS
                        : undefined;

                    return (
                        <ListItemButton
                            key={p.project.id}
                            onClick={() => nav(projectRoute)}
                            sx={{ borderRadius: 1.5, mb: 0.5, py: 0.75, '&:hover': { bgcolor: BG_HOVER } }}
                        >
                            <ListItemIcon sx={{ color: BRAND_RED, minWidth: 28 }}>
                                <MusicNote sx={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={p.project.title || `Project ${p.project.id}`}
                                primaryTypographyProps={{
                                    fontSize: 13,
                                    color: TEXT,
                                    noWrap: true,
                                }}
                                title={p.project.title}
                            />
                        </ListItemButton>
                    );
                })}

                {sortedProjects.length === 0 && (
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, px: 1, display: 'block', mt: 0.5 }}>
                        No projects yet
                    </Typography>
                )}
            </List>
        </Drawer>
    );
}

export default SideBar;
